import React from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import HHMuiSwitch from './HHMuiSwitch'

/**
 * A form switch component that uses MUI's Switch component and integrates with react-hook-form
 * @param {Object} props - Component props
 * @param {string} props.name - Field name
 * @param {Object} props.control - Form control from useFormContext or useForm
 * @param {Object} props.rules - Validation rules
 * @param {string} props.label - Label for the switch
 * @param {string} props.labelPlacement - Placement of the label (start, end, top, bottom)
 * @param {function} props.onChange - Additional onChange handler
 * @param {Object} props.sx - Custom styles
 * @returns {JSX.Element} - Rendered component
 */
const HHFormMuiSwitch = ({ name, control, rules, label, labelPlacement, onChange, sx, ...rest }) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        const { value, onChange: onChangeField, onBlur, ...fieldRest } = field

        // Capitalize only the first letter of the label if it exists
        const formattedLabel = label ? label.charAt(0).toUpperCase() + label.slice(1).toLowerCase() : label

        return (
          <HHMuiSwitch
            checked={value || false}
            onChange={checked => {
              onChangeField(checked)
              if (onChange) {
                onChange(checked)
              }
              onBlur()
            }}
            label={formattedLabel}
            labelPlacement={labelPlacement}
            sx={sx}
            {...fieldRest}
            {...rest}
          />
        )
      }}
    />
  )
}

HHFormMuiSwitch.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  label: PropTypes.string,
  labelPlacement: PropTypes.oneOf(['start', 'end', 'top', 'bottom']),
  onChange: PropTypes.func,
  sx: PropTypes.object,
}

HHFormMuiSwitch.defaultProps = {
  labelPlacement: 'end',
}

export default HHFormMuiSwitch
